import { Flag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { GjeldendeSvar, Sammendrag, SammendragSvar } from '../../api/reise';
import { visSvar } from './SvarOversikt';

/** Svaret i sammendraget, vist med samme tekster som i «Dine svar». */
const somSvar = (s: SammendragSvar): GjeldendeSvar => ({
  sporsmalId: s.sporsmalId,
  instans: s.instans,
  modulId: '',
  modulTittel: '',
  type: s.type,
  tekst: s.sporsmal,
  alternativer: s.valgte,
  profil: s.profil,
  verdi: s.verdi,
  kundensOrd: s.kundensOrd,
  status: s.status,
  vetIkke: s.vetIkke,
  av: s.av,
  tidspunkt: s.tidspunkt,
});

interface Props {
  dokument: Sammendrag;
  /** Gir en «Endre»-knapp ved hvert svar. Uten den er sammendraget bare til å lese. */
  onEndreSvar?: (sporsmalId: string, instans: string) => void;
}

/** Modulene i sammendraget: svarene med kundens egne ord, og flaggene. Brukes av kunden og i arbeidsbordet. */
export default function SammendragModuler({ dokument, onEndreSvar }: Props) {
  const { t } = useTranslation();

  return (
    <>
      {dokument.moduler.map((m) => (
        <div key={m.id} className="mb-6">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="font-semibold text-slate-800">{m.tittel}</h3>
            <span className="text-sm text-slate-600">{t('reise.sammendrag.besvart', { besvart: m.besvart, synlige: m.synlige })}</span>
          </div>
          {m.oppsummering && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="whitespace-pre-line text-slate-800">{m.oppsummering}</p>
              <p className="mt-1 text-xs text-slate-600">{t('reise.sammendrag.aiOppsummering')}</p>
            </div>
          )}
          <ul className="flex flex-col gap-2">
            {m.svar.map((s) => (
              <li
                key={`${s.sporsmalId}|${s.instans}`}
                className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0">
                  {s.profil && <p className="text-sm text-slate-600">{t('reise.oversikt.profil', { profil: s.profil })}</p>}
                  <p className="font-medium">{s.sporsmal}</p>
                  <p className="break-words text-slate-700">{visSvar(somSvar(s), t)}</p>
                  {s.kundensOrd && !['ett_valg', 'flere_valg', 'sjekkliste'].includes(s.type) && (
                    <p className="mt-1 break-words italic text-slate-700">{t('reise.sammendrag.kundensOrd', { tekst: s.kundensOrd })}</p>
                  )}
                </div>
                {onEndreSvar && (
                  <button
                    type="button"
                    onClick={() => onEndreSvar(s.sporsmalId, s.instans)}
                    aria-label={t('reise.sammendrag.endreSvar', { sporsmal: s.sporsmal })}
                    className="knapp-sekundaer shrink-0"
                  >
                    {t('reise.sammendrag.endre')}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {m.flagg.length > 0 && (
            <div className="mt-2 melding-info">
              <p className="mb-1 flex items-center gap-2 font-medium">
                <Flag aria-hidden="true" className="h-4 w-4" />
                {t('reise.sammendrag.flagg')}
              </p>
              <ul className="list-inside list-disc">
                {m.flagg.map((f, i) => (
                  <li key={i}>
                    {t(`reise.flaggtype.${f.type}`, { defaultValue: t('reise.flaggtype.annet') })}
                    {f.sporsmal ? `: ${f.sporsmal}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </>
  );
}
